import DocumentLink from './documents/DocumentLink'
import { useState } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import type { TaskQueueItem } from '../lib/tasks'
import { HelpDot } from './guidance/Guidance'

/* request_unlocked_copy (B7, mig 139) — the officer surface.
 *
 * This task used to fall through to the never-dead-end fallback, where it
 * read as a raw REQUEST_UNLOCKED_COPY chip over a JSON payload and two
 * UUIDs (atlas p5 d10/d12, 2026-07-28) — the last place the machine layer
 * was visible to an officer.
 *
 * The work itself is entirely out-of-band: the upload is encrypted, the
 * pipeline hard-rejected it at intake, nothing about it reached the
 * record, and the officer chases the sender by mail or phone. So the
 * screen's whole job is to say what arrived, why it could not be read,
 * what to ask for, and what happens when the good copy turns up — then
 * let the officer record that they asked. Closing IS the resolution
 * (tasks/router.py::close_informational_task); no record write rides on
 * it, and the pane says so rather than implying a filed decision.
 */

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

export default function UnlockedCopyPane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const documentId = task.target_type === 'document' ? task.target_id : null

  async function markRequested() {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/tasks/${task.task_id}/close`, { method: 'POST' })
      setDone(true)
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div
          data-testid="unlocked-copy-done"
          style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}
        >
          ✓ Recorded — you asked the sender for an unlocked copy.
        </div>
        <div
          style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 8, maxWidth: 560 }}
        >
          When the unlocked copy arrives it is ingested as an ordinary new upload and goes through
          the pipeline like any other document. Nothing further is needed here.
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div
        className="vcard"
        data-testid="unlocked-copy-task"
        style={{ flex: 1, padding: 0, overflow: 'hidden' }}
      >
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
            A file arrived that nobody can open <HelpDot k="pane.unlocked_copy" />
          </div>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
            Ask the sender for a copy without the password.
          </div>
        </div>

        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginBottom: 3 }}>
              The file
            </div>
            <div
              data-testid="unlocked-copy-filename"
              style={{ fontSize: 'var(--text-13)', fontWeight: 600 }}
            >
              {documentId ? (
                <DocumentLink document={{ document_id: documentId }} />
              ) : (
                'The uploaded document'
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginBottom: 3 }}>
              Why it stopped here
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', lineHeight: 1.55 }}>
              It is password-protected, so neither the pipeline nor you can read it. It was refused
              at the door — <strong>nothing from this file reached the record</strong>, and no
              obligation was marked satisfied by it.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginBottom: 3 }}>
              What to ask
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', lineHeight: 1.55 }}>
              Go back to whoever sent it and ask for the same document without the password. Either
              a copy with the protection removed, or the password itself so they can re-send an open
              version — whichever is easier for them.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginBottom: 3 }}>
              What happens next
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', lineHeight: 1.55 }}>
              The unlocked copy arrives as an ordinary new upload and is processed like any other
              document. You do not need to link it back to this task.
            </div>
          </div>
        </div>
      </div>

      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="vcard" style={{ padding: 18 }}>
          {error && (
            <div
              data-testid="unlocked-copy-error"
              style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 10 }}
            >
              {error}
            </div>
          )}
          <button
            className="vbtn pri"
            data-testid="unlocked-copy-requested"
            disabled={busy}
            style={{ width: '100%' }}
            onClick={markRequested}
          >
            I have asked for an unlocked copy
          </button>
          <div
            style={{
              fontSize: 'var(--text-12)',
              color: 'var(--faint)',
              marginTop: 10,
              lineHeight: 1.5,
            }}
          >
            This clears the task from your queue and is recorded under your name. It does not change
            the record — the chase happened outside the system, and the replacement document will
            speak for itself when it arrives.
          </div>
        </div>
      </div>
    </div>
  )
}
